import React from "react";
import { Chart } from "react-google-charts";
import { Typography, Box, CircularProgress } from "@mui/material";

/**
 * Per-day visits bar chart.
 *
 * `metric` selects which series to display:
 *  - "unique" -> Unique Visitors per Day (default)
 *  - "total"  -> Total Visits per Day
 */
const VisitsStackedBarChart = ({
  totalVisits,
  uniqueVisitors,
  visitsByDay,
  uniqueVisitorsByDay,
  loading,
  rangeLabel,
  metric = "unique",
}) => {
  const isUnique = metric === "unique";
  const chartTitle = isUnique ? "Unique Visitors per Day" : "Total Visits per Day";
  const seriesLabel = isUnique ? "Unique Visitors" : "Total Visits";
  const seriesColor = isUnique ? "#00a7cd" : "#00a7cd";
  const byDay = isUnique ? uniqueVisitorsByDay : visitsByDay;

  const avgVisitsPerVisitor =
    uniqueVisitors > 0 ? (totalVisits / uniqueVisitors).toFixed(1) : "0.0";

  // Summary row: date range, total visits, unique visitors, and average visits/visitor.
  const renderSummary = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: 4,
        mb: 1,
        flexWrap: "wrap",
      }}
    >
      {rangeLabel && (
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
          {rangeLabel}
        </Typography>
      )}
      {!isUnique && (
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
          Total Visits: <strong>{totalVisits}</strong>
        </Typography>
      )}
      <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
        Total Unique Visitors: <strong>{uniqueVisitors}</strong>
      </Typography>
      {!isUnique && (
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", fontSize: 13, color: "#666" }}>
          Average Visits/Visitor: <strong>{avgVisitsPerVisitor}</strong>
        </Typography>
      )}
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!byDay || Object.keys(byDay).length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        {renderSummary()}
        <Typography
          variant="h6"
          sx={{ fontFamily: "Avenir Book, sans-serif", color: "#333", mb: 2 }}
        >
          {chartTitle}
        </Typography>
        <Typography sx={{ fontFamily: "Avenir Book, sans-serif", color: "#999" }}>
          No visit data for this period.
        </Typography>
      </Box>
    );
  }

  // Sort days chronologically (use the union of both maps so the axis is complete)
  const sortedDays = Object.keys(byDay).sort();

  const data = [
    ["Date", seriesLabel],
    ...sortedDays.map((dateKey) => {
      const [year, month, day] = dateKey.split("-");
      const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      const formattedDate = date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
      return [formattedDate, byDay[dateKey] || 0];
    }),
  ];

  const options = {
    title: chartTitle,
    titleTextStyle: {
      color: "#333",
      fontSize: 16,
      bold: true,
      fontFamily: "Avenir Book, sans-serif",
    },
    fontFamily: "Avenir Book, sans-serif",
    colors: [seriesColor],
    legend: { position: "none" },
    bar: { groupWidth: "60%" },
    vAxis: {
      minValue: 0,
      format: "0",
      textStyle: { fontFamily: "Avenir Book, sans-serif", fontSize: 12 },
    },
    hAxis: {
      textStyle: {
        fontFamily: "Avenir Book, sans-serif",
        fontSize: 10,
      },
      slantedText: true,
      slantedTextAngle: 45,
    },
    chartArea: { width: "85%", height: "65%" },
    animation: { startup: true, duration: 500, easing: "out" },
  };

  return (
    <Box>
      {renderSummary()}
      {/*
        NOTE: An earlier version rendered a grouped (side-by-side) column chart
        of Total Visits + Unique Visitors, and briefly a LineChart. Those have
        been split into two separate single-series bar charts (see the `metric`
        prop). To restore the combined grouped/line chart, build data with both
        series (["Date", "Total Visits", "Unique Visitors"]) and use ColumnChart
        (grouped) or LineChart here.
      */}
      <Chart
        chartType="ColumnChart"
        width="100%"
        height="300px"
        data={data}
        options={options}
      />
    </Box>
  );
};

export default VisitsStackedBarChart;

